import { useEffect, useRef, useState } from 'react'

/**
 * useEffect hook that does not trigger effect on initial mount.
 *
 * @param effect to be executed when dependencies update
 * @param dependencies variables which trigger the effect when updated
 */
export const useOnUpdate = (effect, dependencies = []) => {
  const didMount = useRef(false)
  useEffect(() => {
    if (didMount.current) {
      effect()
    } else {
      didMount.current = true
    }
  }, dependencies)
}

/**
 * Declarative replacement for useEffect with empty dependency list,
 * i.e. it only runs the effect on mount
 *
 * @param effect
 */
export const useOnMount = effect => useEffect(effect, [])

/**
 * useState with a toggle function instead of setState.
 *
 * @param initialValue
 * @returns {[boolean, function(): void]}
 */
export const useToggle = (initialValue = false) => {
  const [item, setItem] = useState(initialValue)
  const toggle = (value = undefined) =>
    setItem(value === true || (value !== false && !item))

  return [item, toggle]
}

/**
 * useState helper for a state consisting of an array.
 *
 * Returns the state, a function to update state, a function to append a value to state, and a function to remove an index from state
 *
 * @param initialValue
 * @returns {[*[], (function(*[]): void), (function(*): void), (function(int): void)]}
 * @example
 *   const [array, setArray, appendToArray, removeFromArray] = useArrayState([])
 */
export const useArrayState = (initialValue = []) => {
  const [array, setArray] = useState(initialValue)
  const add = next => setArray(old => [...old, next])
  const remove = index =>
    setArray(old => [...old.slice(0, index), ...old.slice(index + 1)])
  return [array, setArray, add, remove]
}

/**
 * Tracks changes in the intersection of a target element.
 *
 * {{{
 * const [loadingRef, loadMore] = useIntersection()
 * useEffect(() => {
 *   //called once the div below becomes visible on screen
 * }, [loadMore])
 * render <div ref={loadingRef} />
 * }}}
 *
 * @param options
 * @returns {[React.MutableRefObject<null>, {}]}
 */
export const useIntersection = (
  options = { root: null, rootMargin: '0px', threshold: 1.0 },
) => {
  const ref = useRef(null)
  const [isIntersecting, setIntersecting] = useState(false)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([{ isIntersecting: intersecting } = {}] = []) =>
        setIntersecting(!!intersecting),
      options,
    )
    if (ref.current) {
      observer.observe(ref.current)
    }
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current)
      }
    }
  }, [ref])
  return [ref, isIntersecting]
}

/**
 * Custom hook that wraps JavaScript's setInterval function
 *
 * {{{
 * useInterval(() => console.log('did tick'), 1000)
 * }}}
 *
 * @param effect Effect to execute every interval tick
 * @param interval Time in milliseconds between every tick
 */
export const useInterval = (effect, interval) => {
  const ref = useRef(null)
  useEffect(() => {
    ref.current = effect
  }, [effect])
  useEffect(() => {
    const timer = ref.current && interval && setInterval(ref.current, interval)
    return () => timer && clearInterval(timer)
  }, [ref.current, interval])
}
